import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { User, AuthContextType, Permission, UserRole, Lead, Organization } from '../types';
import { loginRequest, signUpRequest, fetchLoggedUser, SignUpRequest, getUserOrganizations, switchOrganization, completeWelcome } from '../utils/api';
import { organizationApi } from '../utils/organizationApi';
import { whiteLabelApi } from '../utils/whiteLabelApi';
import { mapBackendRoleToFrontend } from '../utils/roleMapping';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const getPermissions = (role: UserRole): Permission => {
  switch (role) {
    case 'admin':
      return {
        canViewAllLeads: true,
        canEditAllLeads: true,
        canAssignLeads: true,
        canViewTeamLeads: true,
        canEditOwnLeads: true,
        canDeleteLeads: true,
        canViewUnassignedLeads: true,
      };
    case 'gerente':
      return {
        canViewAllLeads: false,
        canEditAllLeads: false,
        canAssignLeads: true,
        canViewTeamLeads: true,
        canEditOwnLeads: true,
        canDeleteLeads: false,
        canViewUnassignedLeads: true,
      };
    case 'vendedor':
      return {
        canViewAllLeads: false,
        canEditAllLeads: false,
        canAssignLeads: false,
        canViewTeamLeads: false,
        canEditOwnLeads: true,
        canDeleteLeads: false,
        canViewUnassignedLeads: true,
      };
    case 'whitelabel':
      return {
        canViewAllLeads: true,
        canEditAllLeads: true,
        canAssignLeads: true,
        canViewTeamLeads: true,
        canEditOwnLeads: true,
        canDeleteLeads: true,
        canViewUnassignedLeads: true,
      };
    default:
      return {
        canViewAllLeads: false,
        canEditAllLeads: false,
        canAssignLeads: false,
        canViewTeamLeads: false,
        canEditOwnLeads: false,
        canDeleteLeads: false,
        canViewUnassignedLeads: false,
      };
  }
};

function mapBackendRoleToLocal(role: string): UserRole {
  return mapBackendRoleToFrontend(role);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [shouldResetPassword, setShouldResetPassword] = useState<boolean | undefined>(undefined);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [availableOrganizations, setAvailableOrganizations] = useState<Organization[]>([]);

  useEffect(() => {
    const init = async () => {
      try {
        const storedToken = localStorage.getItem('authToken');
        if (storedToken) {
          setToken(storedToken);
          try {
            const logged = await fetchLoggedUser();
            const mappedUser: User = {
              id: logged.uid,
              name: logged.data.displayName,
              email: logged.data.email,
              phone: logged.data.phone,
              role: mapBackendRoleToLocal(logged.role),
              shouldResetPassword: logged.shouldResetPassword,
              shouldRedirectWelcome: logged.shouldRedirectWelcome,
              isSuperAdmin: logged.isSuperAdmin,
              isWhiteLabel: logged.isWhiteLabel,
              managedOrganizationIds: logged.managedOrganizationIds,
              isPlanActive: logged.isPlanActive,
            };

            // Carregar informações de créditos para usuários WhiteLabel
            if (mappedUser.isWhiteLabel) {
              try {
                const creditInfo = await whiteLabelApi.getCreditInfo();
                mappedUser.whiteLabelCreditInfo = creditInfo;
              } catch (error) {
                console.warn('Erro ao carregar informações de créditos:', error);
              }
            }

            console.log('AuthContext - User loaded from backend:', {
              shouldRedirectWelcome: logged.shouldRedirectWelcome,
              userRole: mappedUser.role,
              originalRole: logged.role,
              organizationId: logged.organizationId,
              organizationName: logged.organizationName,
              isSuperAdmin: logged.isSuperAdmin,
              isWhiteLabel: logged.isWhiteLabel,
              mappedIsWhiteLabel: mappedUser.isWhiteLabel,
              fullUser: mappedUser
            });
            setUser(mappedUser);
            setIsAuthenticated(true);
            setShouldResetPassword(logged.shouldResetPassword);
            localStorage.setItem('currentUser', JSON.stringify(mappedUser));

            // Load organizations on initialization (after user is set)
            if (mappedUser.isSuperAdmin || mappedUser.isWhiteLabel) {
              try {
                const organizations = await getUserOrganizations();
                setAvailableOrganizations(organizations);

                // Set current organization based on the current session's organization
                if (organizations.length > 0) {
                  if (logged.organizationId) {
                    // Use the organization from the current session
                    const currentOrg = organizations.find(org => org.id === logged.organizationId);

                    if (currentOrg) {
                      setCurrentOrganization(currentOrg);
                      localStorage.setItem('currentOrganization', JSON.stringify(currentOrg));
                    } else {
                      // Fallback to stored organization or first one
                      const storedOrg = localStorage.getItem('currentOrganization');
                      if (storedOrg) {
                        const parsed = JSON.parse(storedOrg) as Organization;
                        const found = organizations.find(org => org.id === parsed.id);
                        if (found) {
                          setCurrentOrganization(found);
                        } else {
                          setCurrentOrganization(organizations[0]);
                          localStorage.setItem('currentOrganization', JSON.stringify(organizations[0]));
                        }
                      } else {
                        setCurrentOrganization(organizations[0]);
                        localStorage.setItem('currentOrganization', JSON.stringify(organizations[0]));
                      }
                    }
                  } else {
                    // No organization ID in session, use stored or first
                    const storedOrg = localStorage.getItem('currentOrganization');
                    if (storedOrg) {
                      const parsed = JSON.parse(storedOrg) as Organization;
                      const found = organizations.find(org => org.id === parsed.id);
                      if (found) {
                        setCurrentOrganization(found);
                      } else {
                        setCurrentOrganization(organizations[0]);
                        localStorage.setItem('currentOrganization', JSON.stringify(organizations[0]));
                      }
                    } else {
                      setCurrentOrganization(organizations[0]);
                      localStorage.setItem('currentOrganization', JSON.stringify(organizations[0]));
                    }
                  }
                }
              } catch (error) {
                console.error('Failed to load organizations during initialization:', error);
              }
            } else if (logged.organizationId) {
              // Para usuários normais, carregar apenas sua organização
              try {
                const organization = await organizationApi.get(logged.organizationId);
                const orgData: Organization = {
                  id: organization.id,
                  name: organization.name || logged.organizationName || 'Organização',
                  identifier: organization.identifier || '',
                  active: organization.active,
                  currentPlan: organization.currentPlan,
                  endIn: organization.endIn,
                  subscriptionActive: organization.subscriptionActive,
                  isWhiteLabelManaged: organization.isWhiteLabelManaged,
                  whiteLabelUserId: organization.whiteLabelUserId
                };
                setCurrentOrganization(orgData);
                localStorage.setItem('currentOrganization', JSON.stringify(orgData));
                console.log('AuthContext - Set current organization for regular user:', orgData);
              } catch (error) {
                console.error('Failed to load organization for regular user:', error);
                // Fallback usando dados do login
                if (logged.organizationName) {
                  const fallbackOrg: Organization = {
                    id: logged.organizationId,
                    name: logged.organizationName,
                    identifier: '',
                    active: true,
                    currentPlan: undefined,
                    endIn: undefined,
                    isWhiteLabelManaged: undefined,
                    whiteLabelUserId: undefined
                  };
                  setCurrentOrganization(fallbackOrg);
                  localStorage.setItem('currentOrganization', JSON.stringify(fallbackOrg));
                  console.log('AuthContext - Set fallback organization:', fallbackOrg);
                }
              }
            }
          } catch (error: any) {
            console.error('Failed to fetch user from backend:', error);

            // Se for erro 401 (token expirado), limpar tudo e deslogar
            if (error?.status === 401 || error?.message?.includes('401')) {
              console.log('Token expired, clearing authentication');
              localStorage.removeItem('authToken');
              localStorage.removeItem('currentUser');
              localStorage.removeItem('currentOrganization');
              setUser(null);
              setToken(null);
              setIsAuthenticated(false);
            } else {
              // Para outros erros, tentar usar dados do localStorage como fallback
              const storedUser = localStorage.getItem('currentUser');
              if (storedUser) {
                const parsed = JSON.parse(storedUser) as User;
                console.log('AuthContext - Fallback to localStorage:', {
                  shouldRedirectWelcome: parsed.shouldRedirectWelcome,
                  userRole: parsed.role
                });
                setUser(parsed);
                setIsAuthenticated(true);
              }
            }
          }
        } else {
          // Sem token armazenado, usuário não está autenticado
          console.log('No token found, user not authenticated');
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch {
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
        localStorage.removeItem('currentUser');
      } finally {
        setIsInitializing(false);
      }
    };
    init();
  }, []);


  const permissions = useMemo(() => {
    return user ? getPermissions(user.role) : getPermissions('vendedor');
  }, [user]);

  const login = async (email: string, password: string, organizationName?: string): Promise<boolean> => {
    try {
      const { token } = await loginRequest(email, password, organizationName);
      localStorage.setItem('authToken', token);
      setToken(token);

      const logged = await fetchLoggedUser();
      const mappedUser: User = {
        id: logged.uid,
        name: logged.data.displayName,
        email: logged.data.email,
        phone: logged.data.phone || '',
        role: mapBackendRoleToLocal(logged.role),
        shouldResetPassword: logged.shouldResetPassword,
        shouldRedirectWelcome: logged.shouldRedirectWelcome,
        isSuperAdmin: logged.isSuperAdmin,
        isWhiteLabel: logged.isWhiteLabel,
        managedOrganizationIds: logged.managedOrganizationIds,
        isPlanActive: logged.isPlanActive,
      };

      // Carregar informações de créditos para usuários WhiteLabel
      if (mappedUser.isWhiteLabel) {
        try {
          const creditInfo = await whiteLabelApi.getCreditInfo();
          mappedUser.whiteLabelCreditInfo = creditInfo;
        } catch (error) {
          console.warn('Erro ao carregar informações de créditos:', error);
        }
      }

      setUser(mappedUser);
      setIsAuthenticated(true);
      setShouldResetPassword(logged.shouldResetPassword);
      localStorage.setItem('currentUser', JSON.stringify(mappedUser));

      // Load available organizations after successful login
      if (mappedUser.isSuperAdmin || mappedUser.isWhiteLabel) {
        try {
          const organizations = await getUserOrganizations();
          setAvailableOrganizations(organizations);

          // Set current organization to the one the user actually logged into
          if (organizations.length > 0 && logged.organizationId) {
            const loggedInOrg = organizations.find(org => org.id === logged.organizationId);

            if (loggedInOrg) {
              setCurrentOrganization(loggedInOrg);
              localStorage.setItem('currentOrganization', JSON.stringify(loggedInOrg));
            } else {
              // Fallback to first organization if the logged-in org is not found
              setCurrentOrganization(organizations[0]);
              localStorage.setItem('currentOrganization', JSON.stringify(organizations[0]));
            }
          } else if (organizations.length > 0) {
            // Fallback if no organizationId in response
            setCurrentOrganization(organizations[0]);
            localStorage.setItem('currentOrganization', JSON.stringify(organizations[0]));
          }
        } catch (error) {
          console.error('Failed to load organizations after login:', error);
        }
      } else if (logged.organizationId) {
        // Para usuários normais, carregar apenas sua organização
        try {
          const organization = await organizationApi.get(logged.organizationId);
          const orgData: Organization = {
            id: organization.id,
            name: organization.name || logged.organizationName || 'Organização',
            identifier: organization.identifier || '',
            active: organization.active,
            currentPlan: organization.currentPlan,
            endIn: organization.endIn,
            isWhiteLabelManaged: organization.isWhiteLabelManaged,
            whiteLabelUserId: organization.whiteLabelUserId
          };
          setCurrentOrganization(orgData);
          localStorage.setItem('currentOrganization', JSON.stringify(orgData));
          console.log('AuthContext - Set current organization for regular user after login:', orgData);
          
          // Verificar se a organização não tem plano e precisa selecionar um
          if (!orgData.currentPlan) {
            localStorage.setItem('needsPlanSelection', 'true');
          } else {
            localStorage.removeItem('needsPlanSelection');
          }
        } catch (error) {
          console.error('Failed to load organization for regular user after login:', error);
          // Fallback usando dados do login
          if (logged.organizationName) {
            const fallbackOrg: Organization = {
              id: logged.organizationId,
              name: logged.organizationName,
              identifier: '',
              active: true,
              currentPlan: undefined,
              endIn: undefined,
              isWhiteLabelManaged: undefined,
              whiteLabelUserId: undefined
            };
            setCurrentOrganization(fallbackOrg);
            localStorage.setItem('currentOrganization', JSON.stringify(fallbackOrg));
            console.log('AuthContext - Set fallback organization after login:', fallbackOrg);
            
            // Se não temos informação do plano, marcar como precisa selecionar
            localStorage.setItem('needsPlanSelection', 'true');
          }
        }
      }

      return true;
    } catch (err) {
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      localStorage.removeItem('currentUser');
      localStorage.removeItem('authToken');
      if (err instanceof Error && err.message.includes('Email não verificado')) {
        throw err;
      }
      return false;
    }
  };

  const signUp = async (signUpData: SignUpRequest): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await signUpRequest(signUpData);

      // Se não há token, significa que o email precisa ser verificado
      if (!response.token) {
        return { success: true };
      }

      localStorage.setItem('authToken', response.token);
      setToken(response.token);

      const logged = await fetchLoggedUser();
      const mappedUser: User = {
        id: logged.uid,
        name: logged.data.displayName,
        email: logged.data.email,
        phone: logged.data.phone || '',
        role: mapBackendRoleToLocal(logged.role),
        shouldResetPassword: logged.shouldResetPassword,
        shouldRedirectWelcome: logged.shouldRedirectWelcome,
        isSuperAdmin: logged.isSuperAdmin,
        isPlanActive: logged.isPlanActive,
      };
      setUser(mappedUser);
      setIsAuthenticated(true);
      setShouldResetPassword(logged.shouldResetPassword);
      localStorage.setItem('currentUser', JSON.stringify(mappedUser));
      localStorage.setItem('isNewUser', 'true');
      return { success: true };
    } catch (err) {
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      localStorage.removeItem('currentUser');
      localStorage.removeItem('authToken');

      console.error('SignUp error:', err);

      let errorMessage = 'Erro ao criar conta. Tente novamente.';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }

      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setCurrentOrganization(null);
    setAvailableOrganizations([]);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentOrganization');
    localStorage.removeItem('hasSeenPlanInactiveModal');
  };

  const loadUserOrganizations = useCallback(async (): Promise<void> => {
    // Only load organizations for SuperAdmin or WhiteLabel users
    if (!user?.isSuperAdmin && !user?.isWhiteLabel && user?.role !== 'whitelabel') {
      return;
    }

    try {
      const organizations = await getUserOrganizations();
      setAvailableOrganizations(organizations);

      // If no current organization is set, set the first one as current
      if (!currentOrganization && organizations.length > 0) {
        const storedOrg = localStorage.getItem('currentOrganization');
        if (storedOrg) {
          const parsed = JSON.parse(storedOrg) as Organization;
          const found = organizations.find(org => org.id === parsed.id);
          if (found) {
            setCurrentOrganization(found);
          } else {
            setCurrentOrganization(organizations[0]);
            localStorage.setItem('currentOrganization', JSON.stringify(organizations[0]));
          }
        } else {
          setCurrentOrganization(organizations[0]);
          localStorage.setItem('currentOrganization', JSON.stringify(organizations[0]));
        }
      }
    } catch (error) {
      console.error('Failed to load user organizations:', error);
    }
  }, [user?.isSuperAdmin, user?.isWhiteLabel, user?.role, currentOrganization]);

  const refreshCurrentOrganization = useCallback(async (): Promise<void> => {
    if (!currentOrganization?.id) {
      return;
    }

    try {
      const updatedOrganization = await organizationApi.get(currentOrganization.id);
      setCurrentOrganization(updatedOrganization);
      localStorage.setItem('currentOrganization', JSON.stringify(updatedOrganization));
    } catch (error) {
      console.error('Failed to refresh current organization:', error);
    }
  }, [currentOrganization?.id]);

  // Load organizations when user changes (for SuperAdmin and WhiteLabel)
  useEffect(() => {
    if (user && (user.isSuperAdmin || user.isWhiteLabel || user.role === 'whitelabel')) {
      loadUserOrganizations();
    }
  }, [user, loadUserOrganizations]);

  const switchOrganizationHandler = async (organizationId: string): Promise<boolean> => {
    // Only SuperAdmin or WhiteLabel users can switch organizations
    if (!user?.isSuperAdmin && !user?.isWhiteLabel && user?.role !== 'whitelabel') {
      console.error('Only SuperAdmin or WhiteLabel users can switch organizations');
      return false;
    }

    try {
      const response = await switchOrganization(organizationId);

      // Update the token
      localStorage.setItem('authToken', response.token);
      setToken(response.token);

      // Update current organization
      const selectedOrg = availableOrganizations.find(org => org.id === organizationId);
      if (selectedOrg) {
        setCurrentOrganization(selectedOrg);
        localStorage.setItem('currentOrganization', JSON.stringify(selectedOrg));
      }

      // Use the user data returned from the switch organization response
      // This avoids the 401 error from calling fetchLoggedUser too quickly
      if (response.user) {
        const mappedUser: User = {
          id: response.user.id,
          name: response.user.name,
          email: response.user.email || response.user.username,
          phone: response.user.phone,
          role: mapBackendRoleToLocal(response.user.role?.toString() || 'sales'),
          shouldResetPassword: response.user.shouldResetPassword,
          isSuperAdmin: response.user.isAdmin,
          isWhiteLabel: response.user.isWhiteLabel,
          managedOrganizationIds: response.user.managedOrganizationIds,
        };
        setUser(mappedUser);
        localStorage.setItem('currentUser', JSON.stringify(mappedUser));
      }

      // Trigger a page reload to ensure all components refresh with new organization context
      // This ensures that all cached data, API calls, and component states are reset
      window.location.reload();

      return true;
    } catch (error) {
      console.error('Failed to switch organization:', error);
      return false;
    }
  };

  const getUsersByRole = (role: UserRole): User[] => {
    // This function is now a synchronous wrapper over cached data use-case.
    // For screens that need dynamic fetch, use the API directly where needed.
    // Keeping signature for backward compatibility; returns empty, consumers should fetch.
    return [] as User[];
  };

  const canAccessLead = (lead: Lead): boolean => {
    if (!user) return false;

    switch (user.role) {
      case 'admin':
        return true;
      case 'gerente':
        // Gerente pode ver leads de sua equipe e não atribuídos
        if (!lead.assignedTo) return true;
        return user.teamMembers?.includes(lead.assignedTo) || false;
      case 'vendedor':
        // Vendedor pode ver seus próprios leads e leads não atribuídos
        return lead.assignedTo === user.id || !lead.assignedTo;
      default:
        return false;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    currentOrganization,
    availableOrganizations,
    login,
    signUp,
    logout,
    isAuthenticated,
    isInitializing,
    permissions,
    getUsersByRole,
    canAccessLead,
    shouldResetPassword,
    loadUserOrganizations,
    refreshCurrentOrganization,
    switchOrganization: switchOrganizationHandler,
    markPasswordResetCompleted: () => {
      setShouldResetPassword(false);
      if (user) {
        const updated = { ...user, shouldResetPassword: false };
        setUser(updated);
        localStorage.setItem('currentUser', JSON.stringify(updated));
      }
    },
    markWelcomeCompleted: async () => {
      try {
        await completeWelcome();
        if (user) {
          const updated = { ...user, shouldRedirectWelcome: false };
          setUser(updated);
          localStorage.setItem('currentUser', JSON.stringify(updated));
        }
      } catch (error) {
        console.error('Failed to complete welcome:', error);
        // Still update locally even if backend call fails
        if (user) {
          const updated = { ...user, shouldRedirectWelcome: false };
          setUser(updated);
          localStorage.setItem('currentUser', JSON.stringify(updated));
        }
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};